
import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Button,
    Label,
    Spinner,
} from "reactstrap";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getAgent, postGenerarReporteAgentes, getReporteAgentesExcel } from "../../helpers/backend";
import Chart from "react-apexcharts";
import { toast, ToastContainer } from "react-toastify";
import "flatpickr/dist/themes/material_blue.css";
import "./ReporteAgentes.css"; // Import styles

const ReporteAgentes = () => {
    const [agents, setAgents] = useState([]);
    const [selectedAgents, setSelectedAgents] = useState([]);
    const [dateRange, setDateRange] = useState([new Date(), new Date()]);
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingAgents, setLoadingAgents] = useState(false);

    useEffect(() => {
        setLoadingAgents(true);
        getAgent()
            .then((res) => {
                const agentList = res.data || [];
                const options = agentList.map((agent) => ({
                    label: agent.full_name || `${agent.first_name} ${agent.last_name}`,
                    value: agent.id
                }));
                setAgents(options);
            })
            .catch((err) => {
                console.error("Error fetching available agents", err);
                toast.error("Error cargando agentes disponibles");
            })
            .finally(() => setLoadingAgents(false));
    }, []);

    const handleGenerateReport = () => {
        if (!selectedAgents.length) {
            toast.warning("Seleccione al menos un agente.");
            return;
        }
        if (!dateRange || dateRange.length < 2) {
            toast.warning("Seleccione un rango de fechas válido.");
            return;
        }

        setLoading(true);
        const payload = {
            agentes_ids: selectedAgents.map((a) => a.value),
            fecha_inicio: dateRange[0].toISOString().split("T")[0],
            fecha_fin: dateRange[1].toISOString().split("T")[0],
        };

        postGenerarReporteAgentes(payload)
            .then((res) => {
                setReportData(res.data || res);
            })
            .catch((err) => {
                console.error("Error generating report", err);
                toast.error("Error generando el reporte.");
            })
            .finally(() => setLoading(false));
    };

    const handleDownloadExcel = () => {
        if (!selectedAgents.length) {
            toast.warning("Seleccione al menos un agente.");
            return;
        }
        if (!dateRange || dateRange.length < 2) {
            toast.warning("Seleccione un rango de fechas válido.");
            return;
        }

        const params = {
            agentes_ids: selectedAgents.map((a) => a.value).join(','),
            fecha_inicio: dateRange[0].toISOString().split("T")[0],
            fecha_fin: dateRange[1].toISOString().split("T")[0],
        };

        toast.info("Iniciando descarga...", { autoClose: 2000 });
        getReporteAgentesExcel(params)
            .then((res) => {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Reporte_Agentes_${params.fecha_inicio}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            })
            .catch((err) => {
                console.error("Error downloading excel", err);
                toast.error("Error al descargar el Excel.");
            });
    };

    // --- Modern Chart Configs ---
    const chartFontFamily = "'Inter', sans-serif";
    const colors = {
        primary: "#4F46E5",
        secondary: "#0EA5E9",
        success: "#10B981",
        warning: "#F59E0B",
        danger: "#F43F5E",
        bg_soft: {
            primary: "#EEF2FF",
            success: "#ECFDF5",
            warning: "#FFFBEB",
            danger: "#FFF1F2"
        }
    };

    const getDonutOptions = (labels) => ({
        labels: labels,
        colors: [colors.secondary, colors.success, colors.warning, colors.danger, colors.primary],
        legend: { show: true, position: "bottom", fontSize: '13px', fontFamily: chartFontFamily, markers: { radius: 12 } },
        plotOptions: {
            pie: {
                donut: {
                    size: "70%",
                    labels: {
                        show: true,
                        name: { show: true, fontSize: '14px', fontFamily: chartFontFamily, color: '#64748B' },
                        value: { show: true, fontSize: '24px', fontFamily: chartFontFamily, fontWeight: 700, color: '#1E293B' },
                        total: { show: true, label: 'Total', fontSize: '14px', fontFamily: chartFontFamily, color: '#64748B' }
                    }
                }
            }
        },
        dataLabels: { enabled: false },
        stroke: { show: false },
    });

    const getBarOptions = (xaxisCategories) => ({
        chart: { toolbar: { show: false }, fontFamily: chartFontFamily },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: "40%",
                borderRadius: 6,
                borderRadiusApplication: 'end',
            }
        },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        xaxis: {
            categories: xaxisCategories,
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: { style: { colors: '#94A3B8', fontSize: '12px' } }
        },
        yaxis: {
            labels: { style: { colors: '#94A3B8', fontSize: '12px' } }
        },
        grid: {
            borderColor: '#F1F5F9',
            strokeDashArray: 4,
            yaxis: { lines: { show: true } }
        },
        colors: [colors.primary, colors.secondary, colors.success],
        fill: { opacity: 1 },
    });

    const getAreaOptions = (categories) => ({
        chart: {
            toolbar: { show: false },
            fontFamily: chartFontFamily,
            zoom: { enabled: false }
        },
        colors: [colors.primary, colors.success],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        fill: {
            type: "gradient",
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.6,
                opacityTo: 0.05,
                stops: [0, 90, 100]
            }
        },
        xaxis: {
            categories: categories,
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: { style: { colors: '#94A3B8', fontSize: '12px' } }
        },
        yaxis: {
            labels: { style: { colors: '#94A3B8', fontSize: '12px' } }
        },
        grid: {
            borderColor: '#F1F5F9',
            strokeDashArray: 4,
        },
        tooltip: {
            theme: 'light',
            style: { fontSize: '12px' },
            x: { format: 'dd MMM' },
        },
        legend: { position: 'top', horizontalAlign: 'right', fontFamily: chartFontFamily }
    });

    return (
        <React.Fragment>
            <div className="ra-page-content page-content">
                <Container fluid>
                    <Breadcrumbs title="Analítica" breadcrumbItem="Productividad de Agentes" />
                    <ToastContainer />

                    {/* Modern Filter Section */}
                    <div className="ra-filter-container">
                        <Row className="align-items-end gy-3">
                            <Col md={4}>
                                <Label className="ra-label">Periodo de Análisis</Label>
                                <Flatpickr
                                    className="form-control"
                                    options={{
                                        mode: "range",
                                        dateFormat: "Y-m-d",
                                        defaultDate: dateRange
                                    }}
                                    value={dateRange}
                                    onChange={(date) => setDateRange(date)}
                                    placeholder="Seleccionar rango..."
                                />
                            </Col>
                            <Col md={4}>
                                <Label className="ra-label">Agentes Operativos</Label>
                                <Select
                                    value={selectedAgents}
                                    isMulti
                                    onChange={(opt) => setSelectedAgents(opt)}
                                    options={agents}
                                    isLoading={loadingAgents}
                                    placeholder="Buscar agentes..."
                                    classNamePrefix="select2-selection"
                                    styles={{
                                        control: (base, state) => ({
                                            ...base,
                                            borderColor: state.isFocused ? colors.primary : '#CBD5E1',
                                            boxShadow: 'none',
                                            borderRadius: '8px',
                                            padding: '2px',
                                            '&:hover': { borderColor: '#94A3B8' }
                                        }),
                                        menu: (base) => ({ ...base, zIndex: 9999, borderRadius: '8px', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }),
                                        multiValue: (base) => ({ ...base, backgroundColor: colors.bg_soft.primary, borderRadius: '4px' }),
                                        multiValueLabel: (base) => ({ ...base, color: colors.primary, fontWeight: 500 }),
                                        multiValueRemove: (base) => ({ ...base, color: colors.primary, ':hover': { backgroundColor: colors.primary, color: 'white' } })
                                    }}
                                />
                            </Col>
                            <Col md={2}>
                                <Button
                                    style={{ backgroundColor: colors.primary, borderColor: colors.primary, borderRadius: '8px', fontWeight: 600, padding: '0.6rem' }}
                                    className="w-100 shadow-sm"
                                    onClick={handleGenerateReport}
                                    disabled={loading}
                                >
                                    {loading ? <Spinner size="sm" color="light" /> : <><i className="bx bx-analyse me-2"></i>Generar</>}
                                </Button>
                            </Col>
                            <Col md={2}>
                                <Button
                                    style={{ backgroundColor: colors.success, borderColor: colors.success, borderRadius: '8px', fontWeight: 600, padding: '0.6rem' }}
                                    className="w-100 shadow-sm"
                                    onClick={handleDownloadExcel}
                                    disabled={loading}
                                >
                                    <i className="bx bx-download me-2"></i>Descargar
                                </Button>
                            </Col>
                        </Row>
                    </div>

                    {reportData && reportData.resumen_real && (
                        <div className="fade-in-up">
                            {/* Context Banner */}
                            <div className="d-flex align-items-center p-3 mb-4 rounded-3" style={{ background: colors.bg_soft.primary, border: `1px solid ${colors.primary}20` }}>
                                <i className="bx bx-info-circle me-3 font-size-20" style={{ color: colors.primary }}></i>
                                <span style={{ color: '#334155', fontWeight: 500 }}>{reportData.resumen_real.explicacion}</span>
                            </div>

                            {/* Stat Cards Grid */}
                            <Row className="mb-4">
                                <Col lg={3} md={6}>
                                    <div className="ra-card ra-stat-card">
                                        <div className="ra-stat-content flex-grow-1">
                                            <p>Total Trabajo</p>
                                            <h4>{reportData.resumen_real.total_trabajo_asignado_o_finalizado}</h4>
                                        </div>
                                        <div className="ra-icon-box ra-icon-primary">
                                            <i className="bx bx-briefcase-alt"></i>
                                        </div>
                                    </div>
                                </Col>
                                <Col lg={3} md={6}>
                                    <div className="ra-card ra-stat-card">
                                        <div className="ra-stat-content flex-grow-1">
                                            <p>Finalizadas OK</p>
                                            <h4 className="text-success">{reportData.resumen_real.finalizadas_exitosas}</h4>
                                        </div>
                                        <div className="ra-icon-box ra-icon-success">
                                            <i className="bx bx-check-double"></i>
                                        </div>
                                    </div>
                                </Col>
                                <Col lg={3} md={6}>
                                    <div className="ra-card ra-stat-card">
                                        <div className="ra-stat-content flex-grow-1">
                                            <p>Incumplidas</p>
                                            <h4 className="text-danger">{reportData.resumen_real.reasignadas_incumplidas}</h4>
                                        </div>
                                        <div className="ra-icon-box ra-icon-danger">
                                            <i className="bx bx-message-square-error"></i>
                                        </div>
                                    </div>
                                </Col>
                                <Col lg={3} md={6}>
                                    <div className="ra-card ra-stat-card">
                                        <div className="ra-stat-content flex-grow-1">
                                            <p>Efectividad</p>
                                            <h4 style={{ color: colors.secondary }}>{reportData.resumen_real.efectividad_real.toFixed(1)}%</h4>
                                        </div>
                                        <div className="ra-icon-box" style={{ background: colors.bg_soft.primary, color: colors.secondary }}>
                                            <i className="bx bx-trending-up"></i>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            {/* Charts Section 1 */}
                            <Row>
                                <Col lg={8}>
                                    <div className="ra-card">
                                        <div className="ra-card-header">
                                            <h5 className="ra-card-title"><i className="bx bx-line-chart text-primary"></i> Evolución Diaria de Productividad</h5>
                                        </div>
                                        <div className="ra-card-body">
                                            <Chart
                                                options={getAreaOptions(reportData.detalle_diario.map(d => d.fecha))}
                                                series={[
                                                    { name: "Trabajo Recibido", data: reportData.detalle_diario.map(d => d.recibidas) },
                                                    { name: "Finalizadas", data: reportData.detalle_diario.map(d => d.finalizadas) }
                                                ]}
                                                type="area"
                                                height={350}
                                            />
                                        </div>
                                    </div>
                                </Col>
                                <Col lg={4}>
                                    <div className="ra-card">
                                        <div className="ra-card-header">
                                            <h5 className="ra-card-title"><i className="bx bx-pie-chart-alt text-primary"></i> Estado de Pendientes</h5>
                                        </div>
                                        <div className="ra-card-body">
                                            <Chart
                                                options={getDonutOptions(reportData.estado_actual_pendientes.map(d => d.estado))}
                                                series={reportData.estado_actual_pendientes.map(d => d.cantidad)}
                                                type="donut"
                                                height={350}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            {/* Charts Section 2 */}
                            <Row>
                                <Col lg={6}>
                                    <div className="ra-card">
                                        <div className="ra-card-header">
                                            <h5 className="ra-card-title">Distribución por Tipo de Caso</h5>
                                        </div>
                                        <div className="ra-card-body">
                                            <Chart
                                                options={getBarOptions(reportData.por_tipo_caso_actual.map(d => d.tipo_caso))}
                                                series={[{ name: "Cantidad", data: reportData.por_tipo_caso_actual.map(d => d.cantidad) }]}
                                                type="bar"
                                                height={320}
                                            />
                                        </div>
                                    </div>
                                </Col>
                                <Col lg={6}>
                                    <div className="ra-card">
                                        <div className="ra-card-header">
                                            <h5 className="ra-card-title">Distribución por Tipo de Orden</h5>
                                        </div>
                                        <div className="ra-card-body">
                                            <Chart
                                                options={getBarOptions(reportData.por_tipo_orden_actual.map(d => d.tipo_orden))}
                                                series={[{ name: "Cantidad", data: reportData.por_tipo_orden_actual.map(d => d.cantidad) }]}
                                                type="bar"
                                                height={320}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            {/* Detailed Table */}
                            <Row>
                                <Col lg={12}>
                                    <div className="ra-card">
                                        <div className="ra-card-header">
                                            <h5 className="ra-card-title"><i className="bx bx-list-ul text-primary"></i> Detalle de Desempeño por Agente</h5>
                                            {/* Placeholder for export button if needed later */}
                                        </div>
                                        <div className="ra-card-body p-0">
                                            <div className="table-responsive">
                                                <table className="ra-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Agente</th>
                                                            <th className="text-center">Trabajo Periodo</th>
                                                            <th className="text-center">Gestiones Totales</th>
                                                            <th className="text-center">Finalizadas</th>
                                                            <th className="text-center">Incumplidas</th>
                                                            <th className="text-center" style={{ width: '25%' }}>Efectividad Real</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {reportData.desempeño_por_agente.map((item, key) => (
                                                            <tr key={key}>
                                                                <td style={{ fontWeight: 600 }}>{item.nombre}</td>
                                                                <td className="text-center">
                                                                    <span className="ra-badge ra-badge-primary">{item.total_trabajo_periodo}</span>
                                                                </td>
                                                                <td className="text-center">{item.total_gestiones_realizadas}</td>
                                                                <td className="text-center">
                                                                    <span className="ra-badge ra-badge-success">{item.finalizadas}</span>
                                                                </td>
                                                                <td className="text-center">
                                                                    {item.reasignadas_incumplidas > 0 &&
                                                                        <span className="ra-badge ra-badge-danger">{item.reasignadas_incumplidas}</span>
                                                                    }
                                                                    {item.reasignadas_incumplidas === 0 && "-"}
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex align-items-center gap-3">
                                                                        <div className="ra-progress-bar">
                                                                            <div
                                                                                className="ra-progress-value"
                                                                                style={{
                                                                                    width: `${item.efectividad_real}%`,
                                                                                    backgroundColor: item.efectividad_real >= 90 ? colors.success : item.efectividad_real >= 70 ? colors.warning : colors.danger
                                                                                }}
                                                                            ></div>
                                                                        </div>
                                                                        <span style={{ fontWeight: 700, minWidth: '45px', textAlign: 'right', fontSize: '13px' }}>
                                                                            {item.efectividad_real.toFixed(1)}%
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ReporteAgentes;
